class TabFeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this.tabs = Array.from(this.querySelectorAll('[data-tab-button]'));
    this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onTabClick.bind(this));
    });

    this.addEventListener('click', (event) => {
      const addButton = event.target.closest('[data-tfc-add]');
      if (addButton) this.addToCart(addButton);
    });
  }

  connectedCallback() {
    this.header = this.querySelector('.tab-featured-collection__tabs');
    if (!this.header) return;
    this.onScroll = () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateSticky();
      });
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);

    // The site header slides in/out on scroll direction: follow it while it animates
    this.siteHeader = document.querySelector('.section-header');
    if (this.siteHeader) {
      this.headerObserver = new MutationObserver(() => {
        const until = Date.now() + 500;
        const follow = () => {
          this.updateSticky();
          if (Date.now() < until) requestAnimationFrame(follow);
        };
        follow();
      });
      this.headerObserver.observe(this.siteHeader, { attributes: true, attributeFilter: ['class'] });
    }
    this.updateSticky();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    cancelAnimationFrame(this.raf);
    this.headerObserver?.disconnect();
  }

  // Release the sticky header once the last product reaches it, so the last card is never covered
  updateSticky() {
    if (!this.header) return;
    // Sit just below the site header while it is showing, flush to the top when it is hidden
    const siteBottom = this.siteHeader ? Math.max(0, this.siteHeader.getBoundingClientRect().bottom) : 0;
    const stickyTop = siteBottom + 11; // 11px breathing room above the pill
    this.header.style.top = `${stickyTop}px`;
    const panel = this.panels.find((item) => !item.hidden);
    const cards = panel?.querySelectorAll('.tab-featured-collection__card');
    const last = cards?.[cards.length - 1];
    const released = last ? last.getBoundingClientRect().top <= this.header.offsetHeight : false;
    this.header.classList.toggle('is-released', released);

    // stick-switch: only while the switcher is actually pinned (sticky layout, not released, at its pinned offset)
    const isSticky = window.getComputedStyle(this.header).position === 'sticky';
    const isStuck = isSticky && !released && this.header.getBoundingClientRect().top <= stickyTop + 0.5;
    this.header.classList.toggle('stick-switch', isStuck);
  }

  // Adds one unit of the card's first available variant, then opens the cart drawer / notification
  addToCart(button) {
    if (button.getAttribute('aria-disabled') === 'true') return;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const spinner = button.querySelector('.loading__spinner');
    const errorEl = button.parentElement.querySelector('.tab-featured-collection__error');
    const variantId = button.dataset.variantId;

    const startedAt = Date.now();
    if (errorEl) errorEl.hidden = true;
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    spinner?.classList.remove('hidden');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    if (button.dataset.preorderProperty) formData.append(`properties[${button.dataset.preorderProperty}]`, 'Pre-order');
    if (cart) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement(document.activeElement);
    }
    config.body = formData;

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'tab-featured-collection',
            productVariantId: variantId,
            errors: response.errors || response.description,
            message: response.message,
          });
          if (errorEl) {
            errorEl.textContent = response.description || response.message;
            errorEl.hidden = false;
          }
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'tab-featured-collection',
          productVariantId: variantId,
          cartData: response,
        });
        cart.renderContents(response);
      })
      .catch((e) => console.error(e))
      .finally(() => {
        if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
        // Keep the loader visible for at least 600ms so it never just flashes
        setTimeout(() => {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          spinner?.classList.add('hidden');
        }, Math.max(0, 600 - (Date.now() - startedAt)));
      });
  }

  onTabClick(event) {
    const selectedTab = event.currentTarget;
    if (selectedTab.getAttribute('aria-selected') === 'true') return;
    this.selectTab(selectedTab);
  }

  selectTab(selectedTab) {
    const targetId = selectedTab.getAttribute('aria-controls');

    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', isSelected);
      tab.classList.toggle('is-active', isSelected);
    });

    this.panels.forEach((panel) => {
      if (panel.id === targetId) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    this.updateSticky?.();
  }
}

// Guarded so the tab sections (collection- or product-based) can share this script on one page
if (!customElements.get('tab-featured-collection')) {
  customElements.define('tab-featured-collection', TabFeaturedCollection);
}
